import { z } from 'zod';
import { AutomatedVoiceVoiceSchema, getAutomatedVoice } from './voices.js';
export const AUTOMATED_VOICE_MESSAGE_CHARACTER_LIMIT = 3000;
const AutomatedVoiceSpeechTextSchema = z
    .string()
    // Reject XML 1.0-invalid characters before trimming or encoding Twilio's speech markup.
    .regex(/^[\t\n\r\u0020-\uD7FF\uE000-\uFFFD\u{10000}-\u{10FFFF}]*$/u, {
    error: 'This message contains unsupported characters.',
    abort: true,
})
    .trim()
    .refine((message) => Array.from(message).length <= AUTOMATED_VOICE_MESSAGE_CHARACTER_LIMIT, {
    error: `Keep this message within ${String(AUTOMATED_VOICE_MESSAGE_CHARACTER_LIMIT)} characters.`,
})
    // JSON Schema's maxLength counts Unicode code points, unlike Zod 4.4's string max.
    .meta({ maxLength: AUTOMATED_VOICE_MESSAGE_CHARACTER_LIMIT });
export var AutomatedVoiceAnswerDetection;
(function (AutomatedVoiceAnswerDetection) {
    AutomatedVoiceAnswerDetection["Human"] = "Human";
    AutomatedVoiceAnswerDetection["Machine"] = "Machine";
    AutomatedVoiceAnswerDetection["Fax"] = "Fax";
    AutomatedVoiceAnswerDetection["Unknown"] = "Unknown";
})(AutomatedVoiceAnswerDetection || (AutomatedVoiceAnswerDetection = {}));
export var AutomatedVoiceVoicemailAction;
(function (AutomatedVoiceVoicemailAction) {
    AutomatedVoiceVoicemailAction["LeaveMessage"] = "LeaveMessage";
    AutomatedVoiceVoicemailAction["EndCall"] = "EndCall";
})(AutomatedVoiceVoicemailAction || (AutomatedVoiceVoicemailAction = {}));
/** Shared so the length check below measures the same markup Primary sends to Twilio. */
export function getAutomatedVoiceProsodyRate(voiceSpeed) {
    return `${String(Math.round(voiceSpeed * 100))}%`;
}
export const AutomatedVoiceMessageSchema = z
    .object({
    message: AutomatedVoiceSpeechTextSchema.min(1),
    language: z.enum(['en', 'es']).default('en'),
    voice: AutomatedVoiceVoiceSchema.optional(),
    voiceSpeed: z.number().min(0.5).max(2).default(1),
    voicemailAction: z.enum(AutomatedVoiceVoicemailAction).optional(),
})
    .superRefine((config, context) => {
    const voice = getAutomatedVoice(config);
    if (!voice.locale.startsWith(`${config.language}-`)) {
        context.addIssue({
            code: 'custom',
            path: ['voice'],
            message: 'Select a voice for the configured language.',
        });
    }
    // Both providers include SSML in the total input limit; Google counts UTF-8 bytes.
    const speechMarkup = `<speak><prosody rate="${getAutomatedVoiceProsodyRate(config.voiceSpeed)}">${config.message
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('\r', '&#13;')}</prosody></speak>`;
    const speechMarkupUtf8Bytes = encodeURIComponent(speechMarkup).replace(/%[\dA-F]{2}/g, '_').length;
    if ((voice.provider === 'Google' && speechMarkupUtf8Bytes > 5000) ||
        (voice.provider === 'Amazon' && Array.from(speechMarkup).length > 6000)) {
        context.addIssue({
            code: 'custom',
            path: ['message'],
            message: 'Shorten this message for the selected voice.',
        });
    }
});
//# sourceMappingURL=automatedVoice.js.map